import React from 'react';
import { Eye, CheckCircle, MousePointerClick, Layers } from 'lucide-react';
import type { Story, StoryStats } from '../../../services/storiesAdminService';

interface StoryAnalyticsPanelProps {
  story: Story;
  stats: StoryStats | null;
  loading: boolean;
}

const Card: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  hint?: string;
}> = ({ icon, label, value, hint }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3">
    <div className="h-10 w-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

const StoryAnalyticsPanel: React.FC<StoryAnalyticsPanelProps> = ({ story, stats, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500">
        Загрузка статистики…
      </div>
    );
  }

  if (story.status !== 'published' && !stats) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="text-gray-400 text-3xl mb-2">📊</div>
        <div className="text-gray-600 font-medium">Аналитика появится после публикации</div>
        <p className="text-sm text-gray-500 mt-1">
          Опубликуйте историю, чтобы начать собирать импрессии и клики.
        </p>
      </div>
    );
  }

  const impressions = stats?.impressions ?? 0;
  const completions = stats?.completions ?? 0;
  const avgSlides = stats?.avg_slides_viewed ?? 0;
  const ctaClicks = stats?.cta_clicks ?? 0;
  const completionRate = impressions > 0 ? ((completions / impressions) * 100).toFixed(1) : '0';
  const ctaCtr = impressions > 0 ? ((ctaClicks / impressions) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card
          icon={<Eye className="h-5 w-5" />}
          label="Импрессии"
          value={impressions.toLocaleString('ru-RU')}
          hint="Уникальные показы истории"
        />
        <Card
          icon={<CheckCircle className="h-5 w-5" />}
          label="Дочитывания"
          value={completions.toLocaleString('ru-RU')}
          hint={`${completionRate}% completion rate`}
        />
        <Card
          icon={<Layers className="h-5 w-5" />}
          label="Среднее число слайдов"
          value={avgSlides.toFixed(1)}
          hint="Сколько слайдов в среднем посмотрели"
        />
        <Card
          icon={<MousePointerClick className="h-5 w-5" />}
          label="CTA-клики"
          value={ctaClicks.toLocaleString('ru-RU')}
          hint={`${ctaCtr}% CTR`}
        />
      </div>
      {impressions === 0 && story.status === 'published' && (
        <p className="text-sm text-gray-500 italic">
          История опубликована, но событий пока нет. Данные появятся после первых показов.
        </p>
      )}
    </div>
  );
};

export default StoryAnalyticsPanel;
